import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  GraduationCap, Plus, ChevronRight, ChevronDown, Loader2, Upload, History,
  Image as ImageIcon, AlertTriangle, CheckCircle2, Rocket, FileStack, Camera,
  ClipboardList, RefreshCw, ArrowLeft, Users, X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const A = `${API}/academy`;

const STATUS_COLOR = { live: "#0E6B3F", production: "#8A6D00", planned: "#8A857A" };
const SHOT_COLOR = { needed: "#8A857A", uploaded: "#8A6D00", approved: "#0E6B3F" };
const CLASSES = ["editorial", "additive", "behavioural", "structural"];

function StatusPill({ status, map = STATUS_COLOR }) {
  return (
    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border"
      style={{ color: map[status] || "#8A857A", borderColor: (map[status] || "#8A857A") + "55" }}>
      {status}
    </span>
  );
}

const TABS = [
  { id: "content", label: "Content", icon: FileStack },
  { id: "shots", label: "Shots", icon: Camera },
  { id: "drift", label: "Drift", icon: AlertTriangle },
  { id: "changelog", label: "SOP Changelog", icon: ClipboardList },
  { id: "versions", label: "Versions", icon: Rocket },
];

export default function AcademyManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState("");
  const [sel, setSel] = useState(null); // product
  const [tab, setTab] = useState("content");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${A}/products`);
      setProducts(data.products || []);
    } catch (e) { toast.error("Could not load Academy products"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seed = async (which) => {
    setSeeding(which);
    try {
      const { data } = await axios.post(`${A}/seed/${which}`);
      toast.success(data.seeded ? `Seeded ${which}: ${data.counts.modules} modules, ${data.counts.shots} shots` : data.message);
      await load();
    } catch (e) { toast.error("Seed failed"); }
    setSeeding("");
  };

  if (sel) return <ProductDetail product={sel} onBack={() => { setSel(null); load(); }} tab={tab} setTab={setTab} />;

  return (
    <div className="space-y-6" data-testid="academy-content">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <GraduationCap className="w-6 h-6" /> Academy Content Studio
          </h1>
          <p className="text-sm text-[#59554D] mt-1">Manage EQUIPR Academy courses, SOP mapping and screenshots. <span className="text-[#8A857A]">Placeholder — local data until the Academy API is live.</span></p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => seed("guardian")} disabled={!!seeding} data-testid="academy-seed-guardian">
            {seeding === "guardian" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Seed Guardian
          </Button>
          <Button variant="outline" onClick={() => seed("core")} disabled={!!seeding} data-testid="academy-seed-core">
            {seeding === "core" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Seed CORE
          </Button>
          <Button variant="outline" onClick={load} data-testid="academy-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#8A857A] py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : products.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-[#8A857A]">
          No products yet. Click <b>Seed Guardian</b> or <b>Seed CORE</b> to load a demo course.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <Card key={p.id} className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => { setTab("content"); setSel(p); }} data-testid={`academy-product-${p.slug}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Badge className="bg-[#1C1C1C] text-[#FFD108] text-[10px]">{p.tag}</Badge>
                  <StatusPill status={p.status} />
                </div>
                <div className="text-lg font-bold text-[#1A1A1A]">{p.name}</div>
                <div className="text-xs text-[#59554D] mt-1 line-clamp-2">{p.blurb}</div>
                <div className="flex items-center gap-4 mt-3 text-xs text-[#59554D]">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {p.roleCount} roles</span>
                  <span>v{p.publishedVersion ?? "—"}</span>
                  {p.driftCount > 0 && <span className="flex items-center gap-1 text-[#C23A00] font-semibold"><AlertTriangle className="w-3 h-3" /> {p.driftCount} drift</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductDetail({ product, onBack, tab, setTab }) {
  return (
    <div className="space-y-4" data-testid="academy-product-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="academy-back"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Badge className="bg-[#1C1C1C] text-[#FFD108] text-[10px]">{product.tag}</Badge>
        <h1 className="text-xl font-bold text-[#1A1A1A]">{product.name}</h1>
        <StatusPill status={product.status} />
      </div>
      <div className="flex gap-1 border-b border-[#DAD5CA]">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`academy-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#1A1A1A] text-[#1A1A1A]" : "border-transparent text-[#8A857A] hover:text-[#59554D]"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "content" && <ContentTab pid={product.id} />}
      {tab === "shots" && <ShotsTab pid={product.id} />}
      {tab === "drift" && <DriftTab pid={product.id} />}
      {tab === "changelog" && <ChangelogTab pid={product.id} />}
      {tab === "versions" && <VersionsTab pid={product.id} product={product} />}
    </div>
  );
}

// ---------------- Content ----------------
function ContentTab({ pid }) {
  const [tree, setTree] = useState(null);
  const [open, setOpen] = useState({});
  const [editMod, setEditMod] = useState(null);
  const loadTree = useCallback(async () => {
    const { data } = await axios.get(`${A}/products/${pid}/tree`); setTree(data);
  }, [pid]);
  useEffect(() => { loadTree(); }, [loadTree]);
  if (!tree) return <Loading />;
  return (
    <div className="space-y-2" data-testid="academy-content-tree">
      {(tree.roles || []).map((r) => (
        <div key={r.id} className="border border-[#DAD5CA] rounded-md bg-white">
          <div className="flex items-center gap-2 px-3 py-2 cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))}>
            {open[r.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span className="font-semibold text-[#1A1A1A]">{r.name}</span>
            <Badge variant="outline" className="text-[9px]">{r.device}</Badge>
            <span className="text-xs text-[#8A857A] ml-auto">{(r.sections || []).reduce((n, s) => n + (s.modules?.length || 0), 0)} modules</span>
          </div>
          {open[r.id] && (r.sections || []).map((s) => (
            <div key={s.id} className="pl-8 pr-3 pb-2">
              <div className="text-xs font-bold uppercase tracking-wide text-[#8A857A] py-1">{s.title}{s.essential && <span className="ml-2 text-[#0E6B3F]">· essential</span>}</div>
              {(s.modules || []).map((m) => (
                <div key={m.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-[#F4F2ED] cursor-pointer" onClick={() => setEditMod(m)} data-testid={`academy-module-${m.slug}`}>
                  <span className="text-sm text-[#1A1A1A] flex-1">{m.title}</span>
                  <Badge variant="outline" className="text-[9px] font-mono">{m.sopId} {m.sopVersion}</Badge>
                  <span className="text-[10px] text-[#8A857A] font-mono">{m.sourceArticleId}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
      {editMod && <ModuleEditor mod={editMod} onClose={() => setEditMod(null)} onSaved={() => { setEditMod(null); loadTree(); }} />}
    </div>
  );
}

function ModuleEditor({ mod, onClose, onSaved }) {
  const [f, setF] = useState({ ...mod });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${A}/modules/${mod.id}`, {
        title: f.title, duration: f.duration, videoUrl: f.videoUrl,
        summaryHtml: f.summaryHtml, sopId: f.sopId, sopVersion: f.sopVersion,
      });
      toast.success("Module saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail?.detail || "Save failed"); }
    setSaving(false);
  };
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle>Edit module</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-4">
          <Field label="Title"><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="academy-mod-title" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SOP ID"><Input value={f.sopId || ""} onChange={(e) => setF({ ...f, sopId: e.target.value })} data-testid="academy-mod-sopid" /></Field>
            <Field label="SOP version"><Input value={f.sopVersion || ""} onChange={(e) => setF({ ...f, sopVersion: e.target.value })} data-testid="academy-mod-sopver" /></Field>
          </div>
          <Field label="sourceArticleId (frozen after publish)"><Input value={f.sourceArticleId || ""} disabled className="font-mono text-xs" /></Field>
          <Field label="Duration"><Input value={f.duration || ""} onChange={(e) => setF({ ...f, duration: e.target.value })} placeholder="4:20" /></Field>
          <Field label="Video URL (Azure — placeholder)"><Input value={f.videoUrl || ""} onChange={(e) => setF({ ...f, videoUrl: e.target.value })} placeholder="https://…" /></Field>
          <Field label="Summary (HTML)"><Textarea rows={5} value={f.summaryHtml || ""} onChange={(e) => setF({ ...f, summaryHtml: e.target.value })} /></Field>
          <Button onClick={save} disabled={saving} className="w-full" data-testid="academy-mod-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save module"}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------- Shots ----------------
function ShotsTab({ pid }) {
  const [shots, setShots] = useState([]);
  const [summary, setSummary] = useState({ total: 0, captured: 0, needed: 0 });
  const [filter, setFilter] = useState("all");
  const [preview, setPreview] = useState(null);
  const fileRefs = useRef({});
  const load = useCallback(async () => {
    const { data } = await axios.get(`${A}/products/${pid}/shots`);
    setShots(data.shots || []); setSummary({ total: data.total, captured: data.captured, needed: data.needed });
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const sops = ["all", ...Array.from(new Set(shots.map((s) => s.sopId)))];
  const shown = filter === "all" ? shots : shots.filter((s) => s.sopId === filter);

  const doUpload = async (shot, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${A}/shots/${shot.id}/upload`, fd);
      toast.success(`Uploaded ${shot.key} (new version)`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  const setStatus = async (shot, status) => {
    try { await axios.put(`${A}/shots/${shot.id}`, { status }); load(); } catch { toast.error("Update failed"); }
  };
  const openPreview = async (shot, version) => {
    try {
      const url = `${A}/shots/${shot.id}/file` + (version ? `?version=${version}` : "");
      const { data } = await axios.get(url, { responseType: "blob" });
      setPreview((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return { shot, url: URL.createObjectURL(data), version: version || shot.currentVersion }; });
    } catch { toast.error("Could not load image"); }
  };
  const closePreview = () => setPreview((p) => { if (p?.url) URL.revokeObjectURL(p.url); return null; });

  return (
    <div className="space-y-3" data-testid="academy-shots">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56" data-testid="academy-shots-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{sops.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All SOPs" : s}</SelectItem>)}</SelectContent>
        </Select>
        <div className="text-sm text-[#59554D]">
          <b>{summary.captured}</b>/{summary.total} captured · <span className="text-[#8A6D00]">{summary.needed} needed</span>
        </div>
      </div>
      <div className="border border-[#DAD5CA] rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F2ED] text-[#59554D] text-xs uppercase">
            <tr><th className="text-left px-3 py-2">SOP</th><th className="text-left px-3 py-2">Filename</th><th className="px-2">Steps</th><th className="px-2">Type</th><th className="px-2">Status</th><th className="px-2">Ver</th><th className="px-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.id} className="border-t border-[#EFECE4]" data-testid={`academy-shot-${s.key}`}>
                <td className="px-3 py-2 font-mono text-xs">{s.sopId}</td>
                <td className="px-3 py-2 font-mono text-xs text-[#1A1A1A]">{s.key}</td>
                <td className="px-2 text-center text-xs text-[#8A857A]">{s.steps}</td>
                <td className="px-2 text-center"><Badge variant="outline" className={`text-[9px] ${s.captureType === "AUTO" ? "text-[#0E6B3F]" : "text-[#8A6D00]"}`}>{s.captureType}</Badge></td>
                <td className="px-2 text-center"><StatusPill status={s.status} map={SHOT_COLOR} /></td>
                <td className="px-2 text-center text-xs">{s.currentVersion || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1 justify-end">
                    <input type="file" accept="image/*" hidden ref={(el) => (fileRefs.current[s.id] = el)}
                      onChange={(e) => { doUpload(s, e.target.files[0]); e.target.value = ""; }} />
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => fileRefs.current[s.id]?.click()} data-testid={`academy-shot-upload-${s.key}`}>
                      <Upload className="w-3 h-3" /> Upload
                    </Button>
                    {s.currentVersion > 0 && <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openPreview(s)} data-testid={`academy-shot-preview-${s.key}`}><ImageIcon className="w-3 h-3" /> v{s.currentVersion}</Button>}
                    {s.status !== "approved" && s.currentVersion > 0 && <Button size="sm" variant="ghost" className="h-7 px-2 text-[#0E6B3F]" onClick={() => setStatus(s, "approved")}><CheckCircle2 className="w-3 h-3" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <Dialog open onOpenChange={closePreview}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-mono text-sm">{preview.shot.key} · v{preview.version}</DialogTitle></DialogHeader>
            <img src={preview.url} alt={preview.shot.key} className="w-full rounded border border-[#DAD5CA]" />
            {(preview.shot.versions || []).length > 1 && (
              <div className="flex gap-1 flex-wrap">
                {preview.shot.versions.map((v) => (
                  <Button key={v.version} size="sm" variant="outline" className="h-7" onClick={() => openPreview(preview.shot, v.version)}>
                    <History className="w-3 h-3" /> v{v.version}
                  </Button>
                ))}
              </div>
            )}
            <div className="text-xs text-[#8A857A]">
              {preview.shot.versions?.map((v) => <div key={v.version}>v{v.version} · {v.filename} · {Math.round(v.bytes / 1024)}KB · {v.uploadedBy} · {(v.uploadedAt || "").slice(0, 10)}</div>)}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---------------- Drift ----------------
function DriftTab({ pid }) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${A}/products/${pid}/drift`).then((r) => setD(r.data)); }, [pid]);
  if (!d) return <Loading />;
  const clean = !d.behindSop.length && !d.orphanModules.length && !d.orphanSops.length;
  return (
    <div className="space-y-4" data-testid="academy-drift">
      {clean && <Card><CardContent className="py-8 text-center text-[#0E6B3F] flex items-center gap-2 justify-center"><CheckCircle2 className="w-5 h-5" /> No drift — every module matches its current SOP version.</CardContent></Card>}
      <DriftList title="Behind SOP (module older than current SOP version)" items={d.behindSop} render={(x) => `${x.sopId}: ${x.title} — module ${x.moduleVersion} vs current ${x.currentVersion}`} color="#C23A00" />
      <DriftList title="Orphan modules (SOP no longer in registry)" items={d.orphanModules} render={(x) => `${x.sopId}: ${x.title}`} color="#8A6D00" />
      <DriftList title="Orphan SOPs (registry SOP with no module — coverage gap)" items={d.orphanSops} render={(x) => `${x.sopId} (current ${x.currentVersion})`} color="#8A6D00" />
    </div>
  );
}
function DriftList({ title, items, render, color }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color }}>{title} · {items.length}</div>
      <div className="border border-[#DAD5CA] rounded-md bg-white divide-y divide-[#EFECE4]">
        {items.map((x, i) => <div key={i} className="px-3 py-2 text-sm text-[#1A1A1A]">{render(x)}</div>)}
      </div>
    </div>
  );
}

// ---------------- Changelog ----------------
function ChangelogTab({ pid }) {
  const [guideVersion, setGuideVersion] = useState("");
  const [rows, setRows] = useState([{ sopId: "", newVersion: "", class: "editorial" }]);
  const [impact, setImpact] = useState(null);
  const [diff, setDiff] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const parseFile = async (fileArg) => {
    const file = fileArg || fileRef.current?.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${A}/products/${pid}/sop-diff`, fd);
      setDiff(data);
      if (data.guideVersion) setGuideVersion(data.guideVersion);
      const prefill = [
        ...data.changed.map((c) => ({ sopId: c.sopId, newVersion: c.newVersion, class: "editorial" })),
        ...data.new.map((n) => ({ sopId: n.sopId, newVersion: n.newVersion, class: "additive" })),
      ];
      setRows(prefill.length ? prefill : [{ sopId: "", newVersion: "", class: "editorial" }]);
      const chg = data.changed.length, nw = data.new.length, rt = data.retired.length;
      toast.success(`Parsed ${data.parsedCount} SOPs — ${chg} changed, ${nw} new, ${rt} retired`);
    } catch (e) { toast.error(e.response?.data?.detail?.detail || "Could not parse SOP file"); }
    setParsing(false);
  };
  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) parseFile(f);
  };

  const submit = async () => {
    try {
      const { data } = await axios.post(`${A}/products/${pid}/sop-changelog`, {
        guideVersion, changes: rows.filter((r) => r.sopId),
      });
      setImpact(data.impact); toast.success("Changelog applied — drift recomputed");
    } catch (e) { toast.error(e.response?.data?.detail?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3 max-w-2xl" data-testid="academy-changelog">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer transition-colors ${drag ? "border-[#1A1A1A] bg-[#F4F2ED]" : "border-[#DAD5CA] hover:border-[#8A857A]"}`}
        data-testid="academy-cl-dropzone">
        <input type="file" accept=".md,.markdown,.txt" hidden ref={fileRef} onChange={() => parseFile()} data-testid="academy-cl-fileinput" />
        {parsing ? <div className="flex items-center gap-2 justify-center text-[#59554D]"><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</div>
          : <div className="text-sm text-[#59554D]"><Upload className="w-5 h-5 mx-auto mb-1 text-[#8A857A]" /> Drag &amp; drop the new SOP overview <b>.md</b> here, or click to browse. <div className="text-xs text-[#8A857A] mt-1">Auto-detects changed / new / retired SOPs against the registry.</div></div>}
      </div>

      {diff && (
        <div className="border border-[#DAD5CA] rounded-md bg-white p-3 text-sm space-y-1" data-testid="academy-cl-diffsummary">
          <div className="text-xs font-bold uppercase text-[#8A857A]">Parse summary · {diff.parsedCount} SOPs</div>
          <div className="flex flex-wrap gap-3">
            <span className="text-[#C23A00]">{diff.changed.length} changed</span>
            <span className="text-[#0E6B3F]">{diff.new.length} new</span>
            <span className="text-[#8A6D00]">{diff.retired.length} retired</span>
            <span className="text-[#8A857A]">{diff.unchangedCount} unchanged</span>
          </div>
          {diff.retired.length > 0 && (
            <div className="text-xs text-[#8A6D00] pt-1">
              Retired (no longer in the overview — retire these modules manually): {diff.retired.map((r) => r.sopId).join(", ")}
            </div>
          )}
        </div>
      )}

      <Field label="Guide version"><Input value={guideVersion} onChange={(e) => setGuideVersion(e.target.value)} placeholder="Guardian Guide v1.2" data-testid="academy-cl-guideversion" /></Field>
      <div className="text-xs font-bold uppercase text-[#8A857A]">Changed / new SOPs — set class &amp; review before applying</div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_1.2fr_auto] gap-2 items-center">
          <Input placeholder="SOP-002" value={r.sopId} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, sopId: e.target.value } : x))} />
          <Input placeholder="v2.0" value={r.newVersion} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, newVersion: e.target.value } : x))} />
          <Select value={r.class} onValueChange={(v) => setRows(rows.map((x, j) => j === i ? { ...x, class: v } : x))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setRows([...rows, { sopId: "", newVersion: "", class: "editorial" }])}><Plus className="w-4 h-4" /> Row</Button>
        <Button size="sm" onClick={submit} data-testid="academy-cl-submit">Apply changelog</Button>
      </div>
      {impact && (
        <div className="border border-[#DAD5CA] rounded-md bg-white p-3 space-y-2">
          <div className="text-xs font-bold uppercase text-[#8A857A]">Impact</div>
          {impact.map((x, i) => (
            <div key={i} className="text-sm">
              <b className="font-mono">{x.sopId}</b> → {x.newVersion} <Badge variant="outline" className="text-[9px]">{x.class}</Badge>
              {x.isNewSop ? <span className="text-[#8A6D00]"> · new SOP (no module yet — coverage gap)</span>
                : <span className="text-[#59554D]"> · {x.affectedModules.length} module(s) now behind: {x.affectedModules.map((m) => m.title).join(", ")}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Versions ----------------
function VersionsTab({ pid, product }) {
  const [versions, setVersions] = useState([]);
  const [label, setLabel] = useState("");
  const [cls, setCls] = useState("editorial");
  const [recert, setRecert] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { const { data } = await axios.get(`${A}/products/${pid}/versions`); setVersions(data.versions || []); }, [pid]);
  useEffect(() => { load(); }, [load]);
  const publish = async (ack = false) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${A}/products/${pid}/publish`, { label, changeClass: cls, reCertify: recert, acknowledgeWarnings: ack });
      toast.success(`Published ${data.label} (v${data.version})${data.reCertify ? " · re-cert triggered" : ""}`);
      setLabel(""); load();
    } catch (e) {
      const w = e.response?.data?.detail?.warnings;
      if (w && !ack) { setBusy(false); if (window.confirm("Warnings:\n" + w.join("\n") + "\n\nPublish anyway?")) return publish(true); return; }
      else toast.error(e.response?.data?.detail?.detail || "Publish failed");
    }
    setBusy(false);
  };
  const setLive = async () => {
    try { await axios.post(`${A}/products/${pid}/set-live`); toast.success("Product set live"); }
    catch (e) { const u = e.response?.data?.detail?.unmetGates; toast.error(u ? "Unmet gates: " + u.join(", ") : "Failed"); }
  };
  const gates = product.gates || {};
  return (
    <div className="space-y-4 max-w-2xl" data-testid="academy-versions">
      <div className="border border-[#DAD5CA] rounded-md bg-white p-3 space-y-2">
        <div className="text-xs font-bold uppercase text-[#8A857A]">Publish a new version</div>
        <Field label="Label (mirrors SOP guide version)"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Guardian Guide v1.1" data-testid="academy-publish-label" /></Field>
        <div className="grid grid-cols-2 gap-3 items-end">
          <Field label="Change class">
            <Select value={cls} onValueChange={setCls}><SelectTrigger data-testid="academy-publish-class"><SelectValue /></SelectTrigger>
              <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </Field>
          <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={recert} onChange={(e) => setRecert(e.target.checked)} data-testid="academy-publish-recert" /> Trigger re-certification</label>
        </div>
        <Button onClick={() => publish(false)} disabled={busy || !label} data-testid="academy-publish"><Rocket className="w-4 h-4" /> Publish</Button>
      </div>

      <div className="border border-[#DAD5CA] rounded-md bg-white p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase text-[#8A857A]">Go-live gates</div>
          <Button size="sm" variant="outline" onClick={setLive} data-testid="academy-setlive">Set live</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {["guideExists", "rolesNamed", "essentialsFlagged", "demoDataSeeded", "contentOwnerAssigned"].map((g) => (
            <Badge key={g} variant="outline" className={`text-[10px] ${gates[g] ? "text-[#0E6B3F]" : "text-[#8A857A]"}`}>{gates[g] ? "✓" : "○"} {g}</Badge>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs font-bold uppercase text-[#8A857A] mb-1">Published versions</div>
        {versions.length === 0 ? <div className="text-sm text-[#8A857A]">None yet.</div> : (
          <div className="border border-[#DAD5CA] rounded-md bg-white divide-y divide-[#EFECE4]">
            {versions.map((v) => (
              <div key={v.id} className="px-3 py-2 text-sm flex items-center gap-2">
                <b>v{v.version}</b> <span>{v.label}</span>
                <Badge variant="outline" className="text-[9px]">{v.changeClass}</Badge>
                {v.reCertify && <Badge className="bg-[#C23A00] text-white text-[9px]">re-cert</Badge>}
                <span className="text-xs text-[#8A857A] ml-auto">{(v.publishedAt || "").slice(0, 10)} · {v.publishedBy}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs text-[#59554D] block mb-1">{label}</label>{children}</div>;
}
function Loading() {
  return <div className="flex items-center gap-2 text-[#8A857A] py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
}
